import { useState, useEffect } from "react";
import {
  MapPin,
  Plus,
  Edit,
  Trash2,
  Building2,
  Phone,
  Mail,
  Clock,
  Check,
  X,
  AlertCircle,
  Upload,
  Image,
  Smartphone,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useLocation } from "@/contexts/LocationContext";
import { useConfirm } from "@/contexts/ConfirmContext";
import { usePermissions } from "@/hooks/usePermissions";
import { getBrowserTimezone } from "@/lib/timezones";
import TimezoneSelect from "@/components/TimezoneSelect";

interface LocationData {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  coverImage: string | null;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string | null;
  phone: string;
  email: string;
  timezone: string;
  isActive: boolean;
  showInApp: boolean;
  whatsappEnabled?: boolean;
  voiceAiMapUrl?: string | null;
  createdAt: string;
  // Solo lo manda `/admin/all-locations`: el superadmin ve negocios de los dos
  // modos en la misma lista, así que cada ficha necesita el modo de SU negocio.
  organization?: {
    id: string;
    name: string;
    slug: string;
    subscriptionStatus: string;
    bookingMode?: "tables" | "appointments";
  };
  _count?: {
    reservations: number;
    appointments?: number;
  };
}

/**
 * Cuánto se ha usado la sede, con la palabra que toca. Las reservas de mesa y
 * las citas son modelos distintos: un centro de citas tiene SIEMPRE 0 reservas,
 * así que la ficha de Centro Elle enseñaba "0 reservas totales" teniendo 318
 * citas (19-sep-2026).
 */
function resumenDeUso(
  location: LocationData,
  modoPorDefecto: "tables" | "appointments",
): string {
  const modo = location.organization?.bookingMode ?? modoPorDefecto;
  const esCitas = modo === "appointments";
  const total = esCitas
    ? (location._count?.appointments ?? 0)
    : (location._count?.reservations ?? 0);

  if (total === 0) return esCitas ? "Sin citas todavía" : "Sin reservas todavía";
  if (total === 1) return esCitas ? "1 cita en total" : "1 reserva en total";
  return esCitas ? `${total} citas en total` : `${total} reservas en total`;
}

interface FormData {
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  phone: string;
  email: string;
  timezone: string;
  whatsappEnabled: boolean;
  voiceAiMapUrl: string;
}

const emptyForm: FormData = {
  name: "",
  address: "",
  city: "",
  state: "",
  country: "España",
  postalCode: "",
  phone: "",
  email: "",
  timezone: getBrowserTimezone(),
  whatsappEnabled: true,
  voiceAiMapUrl: "",
};

export default function Locations() {
  const { can } = usePermissions();
  const { confirm } = useConfirm();
  const {
    locations,
    currentLocation,
    setCurrentLocation,
    refreshLocations,
    canCreateLocation,
    maxLocations,
    isLoading,
    currentBookingMode,
  } = useLocation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<LocationData | null>(
    null,
  );
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState<string | null>(null); // locationId being uploaded
  const [coverLoading, setCoverLoading] = useState<string | null>(null); // locationId being uploaded (cover)
  const [appToggleLoading, setAppToggleLoading] = useState<string | null>(null); // locationId being toggled

  const isAdmin = can("sedes:gestionar");

  // Handle logo upload
  const handleLogoUpload = async (
    locationId: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/gif",
      "image/webp",
      "image/svg+xml",
    ];
    if (!validTypes.includes(file.type)) {
      setError("Formato de imagen no válido. Usa PNG, JPEG, GIF, WebP o SVG");
      return;
    }

    setLogoLoading(locationId);
    setError(null);

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result as string;
        try {
          await apiClient.put(`/locations/${locationId}/logo`, {
            logo: base64,
          });
          await refreshLocations();
          setSuccess("Logo actualizado correctamente");
        } catch (err) {
          const error = err as { response?: { data?: { message?: string } } };
          setError(error.response?.data?.message || "Error al subir el logo");
        } finally {
          setLogoLoading(null);
        }
      };
      reader.onerror = () => {
        setError("Error al leer el archivo");
        setLogoLoading(null);
      };
      reader.readAsDataURL(file);
    } catch {
      setError("Error de conexión");
      setLogoLoading(null);
    }

    // Reset input
    if (e.target) {
      e.target.value = "";
    }
  };

  const handleRemoveLogo = async (locationId: string) => {
    if (!(await confirm({ title: "¿Eliminar el logo?", message: "Se eliminará el logo del local.", confirmText: "Sí, eliminar", danger: true }))) return;

    setLogoLoading(locationId);
    try {
      await apiClient.put(`/locations/${locationId}/logo`, { logo: null });
      await refreshLocations();
      setSuccess("Logo eliminado correctamente");
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || "Error al eliminar el logo");
    } finally {
      setLogoLoading(null);
    }
  };

  // Handle cover image upload
  const handleCoverUpload = async (
    locationId: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setError("Formato no válido. Usa PNG, JPEG o WebP");
      return;
    }

    setCoverLoading(locationId);
    setError(null);

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result as string;
        try {
          await apiClient.put(`/locations/${locationId}/cover`, {
            coverImage: base64,
          });
          await refreshLocations();
          setSuccess("Foto de portada actualizada correctamente");
        } catch (err) {
          const error = err as { response?: { data?: { message?: string } } };
          setError(
            error.response?.data?.message ||
              "Error al subir la foto de portada",
          );
        } finally {
          setCoverLoading(null);
        }
      };
      reader.onerror = () => {
        setError("Error al leer el archivo");
        setCoverLoading(null);
      };
      reader.readAsDataURL(file);
    } catch {
      setError("Error de conexión");
      setCoverLoading(null);
    }

    if (e.target) e.target.value = "";
  };

  const handleRemoveCover = async (locationId: string) => {
    if (!(await confirm({ title: "¿Eliminar la foto de portada?", message: "Se eliminará la imagen de portada del local.", confirmText: "Sí, eliminar", danger: true }))) return;
    setCoverLoading(locationId);
    try {
      await apiClient.put(`/locations/${locationId}/cover`, {
        coverImage: null,
      });
      await refreshLocations();
      setSuccess("Foto de portada eliminada");
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(
        error.response?.data?.message || "Error al eliminar la foto de portada",
      );
    } finally {
      setCoverLoading(null);
    }
  };

  const handleOpenCreate = () => {
    if (!canCreateLocation) {
      setError(
        `Has alcanzado el límite de ${maxLocations} local${maxLocations !== 1 ? "es" : ""} de tu plan. Mejora tu suscripción para añadir más.`,
      );
      return;
    }
    setEditingLocation(null);
    setFormData(emptyForm);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (location: LocationData) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address,
      city: location.city,
      state: location.state,
      country: location.country,
      postalCode: location.postalCode || "",
      phone: location.phone,
      email: location.email,
      timezone: location.timezone,
      whatsappEnabled: location.whatsappEnabled ?? true,
      voiceAiMapUrl: location.voiceAiMapUrl || "",
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (editingLocation) {
        await apiClient.put(`/locations/${editingLocation.id}`, formData);
        setSuccess("Local actualizado correctamente");
      } else {
        await apiClient.post("/locations", formData);
        setSuccess("Local creado correctamente");
      }

      await refreshLocations();
      setIsModalOpen(false);
      setFormData(emptyForm);
      setEditingLocation(null);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(
        error.response?.data?.message || "Error al guardar el local",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (location: LocationData) => {
    if (
      !(await confirm({
        title: `¿Eliminar "${location.name}"?`,
        message: "Esta acción desactivará el local.",
        confirmText: "Sí, eliminar",
        danger: true,
      }))
    ) {
      return;
    }

    try {
      await apiClient.delete(`/locations/${location.id}`);
      setSuccess("Local eliminado correctamente");
      await refreshLocations();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(
        error.response?.data?.message || "Error al eliminar el local",
      );
    }
  };

  const handleToggleShowInApp = async (location: LocationData) => {
    setAppToggleLoading(location.id);
    setError(null);
    try {
      await apiClient.patch(`/locations/${location.id}/show-in-app`);
      await refreshLocations();
      setSuccess(
        location.showInApp
          ? `"${location.name}" ya no aparece en la app`
          : `"${location.name}" ahora es visible en la app`,
      );
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(
        error.response?.data?.message || "Error al actualizar la visibilidad",
      );
    } finally {
      setAppToggleLoading(null);
    }
  };

  const handleSelectLocation = (location: LocationData) => {
    setCurrentLocation(location);
    setSuccess(`Ahora estás gestionando "${location.name}"`);
  };

  // Clear messages after 4 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Ficha del negocio</h1>
          <p className="mt-1 text-sm text-gray-500">
            Dirección, teléfono, logo, foto de portada y visibilidad en la app ·{" "}
            {locations.length} de {maxLocations} local
            {maxLocations !== 1 ? "es" : ""} de tu plan
          </p>
        </div>

        {isAdmin && (
          <button
            onClick={handleOpenCreate}
            disabled={!canCreateLocation}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              canCreateLocation
                ? "bg-primary-600 text-white hover:bg-primary-700"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            <Plus size={20} />
            Nuevo local
          </button>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check size={20} />
          {success}
        </div>
      )}

      {/* Locations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {locations.map((location) => (
          <div
            key={location.id}
            className={`bg-white rounded-xl shadow-sm border-2 transition-all hover:shadow-md ${
              currentLocation?.id === location.id
                ? "border-primary-500 ring-2 ring-primary-100"
                : "border-gray-100"
            }`}
          >
            {/* Current indicator */}
            {currentLocation?.id === location.id && (
              <div className="bg-primary-500 text-white text-xs font-medium px-3 py-1 rounded-t-lg text-center">
                Local activo
              </div>
            )}

            <div className="p-5">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {/* Logo section */}
                  <div className="relative group">
                    {location.logo ? (
                      <img
                        src={location.logo}
                        alt={`Logo de ${location.name}`}
                        className="w-12 h-12 rounded-xl object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                        <Building2 className="text-primary-600" size={24} />
                      </div>
                    )}
                    {/* Upload overlay */}
                    {isAdmin && (
                      <label
                        className={`absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity ${logoLoading === location.id ? "opacity-100" : ""}`}
                        title={location.logo ? "Cambiar logo" : "Subir logo"}
                      >
                        {logoLoading === location.id ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Upload className="text-white" size={16} />
                        )}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
                          className="hidden"
                          onChange={(e) => handleLogoUpload(location.id, e)}
                          disabled={logoLoading !== null}
                        />
                      </label>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {location.name}
                    </h3>
                    <p className="text-sm text-gray-500">{location.slug}</p>
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex gap-1">
                    {location.logo && (
                      <button
                        onClick={() => handleRemoveLogo(location.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar logo"
                        disabled={logoLoading !== null}
                      >
                        <Image size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => handleOpenEdit(location)}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit size={16} />
                    </button>
                    {locations.length > 1 && (
                      <button
                        onClick={() => handleDelete(location)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Foto de portada (cover image) */}
              {isAdmin && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Foto de portada (app móvil)
                    </span>
                    <div className="flex gap-1">
                      <label
                        className="text-xs text-primary-600 hover:text-primary-700 cursor-pointer font-medium flex items-center gap-1"
                        title="Subir foto de portada"
                      >
                        {coverLoading === location.id ? (
                          <div className="w-3 h-3 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Upload size={12} />
                        )}
                        {location.coverImage ? "Cambiar" : "Subir"}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          className="hidden"
                          onChange={(e) => handleCoverUpload(location.id, e)}
                          disabled={coverLoading !== null}
                        />
                      </label>
                      {location.coverImage && (
                        <button
                          onClick={() => handleRemoveCover(location.id)}
                          className="text-xs text-red-500 hover:text-red-600 font-medium"
                          disabled={coverLoading !== null}
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </div>
                  {location.coverImage ? (
                    <img
                      src={location.coverImage}
                      alt="Foto de portada"
                      className="w-full h-24 object-cover rounded-lg border border-gray-200"
                    />
                  ) : (
                    <label className="w-full h-24 border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center gap-1 text-gray-400 cursor-pointer hover:border-primary-300 hover:bg-primary-50 hover:text-primary-500 transition-all">
                      <Image size={20} />
                      <span className="text-xs">
                        Foto horizontal para la app
                      </span>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        className="hidden"
                        onChange={(e) => handleCoverUpload(location.id, e)}
                        disabled={coverLoading !== null}
                      />
                    </label>
                  )}
                </div>
              )}

              {/* Details */}
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                  <span className="truncate">
                    {location.address}, {location.city}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={14} className="text-gray-400 flex-shrink-0" />
                  <span>{location.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={14} className="text-gray-400 flex-shrink-0" />
                  <span className="truncate">{location.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-gray-400 flex-shrink-0" />
                  <span>{location.timezone}</span>
                </div>
              </div>

              {/* App visibility toggle */}
              {isAdmin && (
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <Smartphone
                      size={15}
                      className={
                        location.showInApp
                          ? "text-primary-600"
                          : "text-gray-400"
                      }
                    />
                    <span
                      className={
                        location.showInApp
                          ? "text-gray-700 font-medium"
                          : "text-gray-400"
                      }
                    >
                      {location.showInApp
                        ? "Visible en la app"
                        : "Oculta en la app"}
                    </span>
                  </div>
                  <button
                    onClick={() => handleToggleShowInApp(location)}
                    disabled={appToggleLoading === location.id}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                      location.showInApp ? "bg-primary-600" : "bg-gray-300"
                    } ${appToggleLoading === location.id ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                    title={
                      location.showInApp
                        ? "Desactivar en la app"
                        : "Activar en la app"
                    }
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                        location.showInApp
                          ? "translate-x-4.5"
                          : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>
              )}

              {/* Stats */}
              {location._count && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    {resumenDeUso(location, currentBookingMode)}
                  </p>
                </div>
              )}

              {/* Select Button */}
              {currentLocation?.id !== location.id && (
                <button
                  onClick={() => handleSelectLocation(location)}
                  className="mt-4 w-full py-2 px-4 bg-gray-50 hover:bg-primary-50 text-gray-700 hover:text-primary-700 rounded-lg font-medium transition-colors text-sm"
                >
                  Seleccionar este local
                </button>
              )}
            </div>
          </div>
        ))}

        {/* Add new location card */}
        {canCreateLocation && isAdmin && (
          <button
            onClick={handleOpenCreate}
            className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl p-8 hover:border-primary-300 hover:bg-primary-50 transition-all flex flex-col items-center justify-center gap-3 text-gray-500 hover:text-primary-600"
          >
            <div className="w-16 h-16 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center">
              <Plus size={32} />
            </div>
            <span className="font-medium">Añadir local</span>
          </button>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90dvh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingLocation ? "Editar local" : "Nuevo local"}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del local *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Mi Negocio"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección *
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Calle Principal, 123"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enlace de Google Maps
                </label>
                <input
                  type="url"
                  value={formData.voiceAiMapUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, voiceAiMapUrl: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="https://maps.app.goo.gl/..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Pega el enlace de tu negocio en Google Maps: de ahí sacamos la
                  ubicación exacta para el mapa de la app. Es el mismo enlace que
                  usa la centralita IA.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ciudad *
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) =>
                      setFormData({ ...formData, city: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Madrid"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Provincia *
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) =>
                      setFormData({ ...formData, state: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Madrid"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    País
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) =>
                      setFormData({ ...formData, country: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="España"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código Postal
                  </label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) =>
                      setFormData({ ...formData, postalCode: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="28001"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="+34 912 345 678"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="contacto@minegocio.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Zona horaria
                </label>
                <TimezoneSelect
                  value={formData.timezone}
                  onChange={(tz) =>
                    setFormData({ ...formData, timezone: tz })
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  Determina la hora local de las reservas y avisos de esta
                  sede.
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {isSubmitting
                    ? "Guardando..."
                    : editingLocation
                      ? "Guardar Cambios"
                      : "Crear local"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
